"use client";

import { useCallback, useEffect, useMemo, useState } from "react";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Box from "@mui/material/Box";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import Chip from "@mui/material/Chip";
import Alert from "@mui/material/Alert";
import CircularProgress from "@mui/material/CircularProgress";
import Pagination from "@mui/material/Pagination";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import TextField from "@mui/material/TextField";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import RefreshIcon from "@mui/icons-material/Refresh";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import HistoryIcon from "@mui/icons-material/History";
import Button from "@mui/material/Button";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import DataTable from "examples/Tables/DataTable";
import ComplexStatisticsCard from "examples/Cards/StatisticsCards/ComplexStatisticsCard";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const BASE_URL = process.env.REACT_APP_BASE_URL;
const token = sessionStorage.getItem("token");

const TAB_KEYS = ["reconciled", "unreconciled", "duplicate", "mismatch"];
const PAGE_SIZE_OPTIONS = [5, 10, 25, 50, 100];
const DEFAULT_BANKS = ["HBL", "UBL", "NBP", "BAL"];

const formatAmount = (value) => {
  if (value === null || value === undefined || value === "") return "-";
  const num = Number.parseFloat(value);
  if (Number.isNaN(num)) return value;
  return num.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const formatDate = (value) => {
  if (!value) return "-";
  const parsed = new Date(value.includes("T") ? value : `${value}T00:00:00`);
  if (Number.isNaN(parsed.getTime())) return value;
  return parsed.toLocaleDateString();
};

const formatDateTime = (value) => {
  if (!value) return "-";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  return parsed.toLocaleString();
};

const statusChip = (label, color) => (
  <Chip label={label} color={color} size="small" variant="outlined" />
);

const defaultFilters = {
  voucherSearch: "",
  startDate: "",
  endDate: "",
  bank: "all",
  cmsStatus: "all",
  bankStatus: "all",
};

function ReconciliationDashboard() {
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [tableLoading, setTableLoading] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [filters, setFilters] = useState(defaultFilters);
  const [appliedFilters, setAppliedFilters] = useState(defaultFilters);
  const [banks, setBanks] = useState(DEFAULT_BANKS);
  const [pagination, setPagination] = useState({
    page: 1,
    page_size: 10,
    total: 0,
    total_pages: 0,
  });
  const [summary, setSummary] = useState({
    reconciled: 0,
    unreconciled: 0,
    duplicate: 0,
    mismatch: 0,
    total_cms_vouchers: 0,
    total_bank_records: 0,
  });
  const [records, setRecords] = useState([]);

  const [markPaidOpen, setMarkPaidOpen] = useState(false);
  const [auditOpen, setAuditOpen] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [auditLogs, setAuditLogs] = useState([]);
  const [auditLoading, setAuditLoading] = useState(false);
  const [markPaidForm, setMarkPaidForm] = useState({
    bank: DEFAULT_BANKS[0],
    transaction_id: "",
    payment_date: new Date().toISOString().slice(0, 10),
  });

  const currentTabKey = TAB_KEYS[activeTab];

  const buildQueryParams = useCallback(
    (category, pageNumber, size, filterValues = appliedFilters) => {
      const params = new URLSearchParams({
        category,
        page: String(pageNumber),
        page_size: String(size),
        cms_status: filterValues.cmsStatus,
        bank_status: filterValues.bankStatus,
        bank: filterValues.bank,
      });
      if (filterValues.startDate) params.set("start_date", filterValues.startDate);
      if (filterValues.endDate) params.set("end_date", filterValues.endDate);
      if (filterValues.voucherSearch?.trim()) params.set("search", filterValues.voucherSearch.trim());
      return params;
    },
    [appliedFilters]
  );

  const fetchDashboard = useCallback(
    async ({
      category = currentTabKey,
      pageNumber = page,
      size = pageSize,
      filterValues = appliedFilters,
      refreshAll = false,
    } = {}) => {
      if (refreshAll) setSummaryLoading(true);
      setTableLoading(true);

      try {
        const params = buildQueryParams(category, pageNumber, size, filterValues);
        const response = await fetch(
          `${BASE_URL}/fee_voucher/get_reconciliation_dashboard?${params.toString()}`,
          {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
          }
        );
        const result = await response.json();

        if (response.ok) {
          setSummary(result.summary || {});
          setRecords(result.records || []);
          setPagination(result.pagination || { page: pageNumber, page_size: size, total: 0, total_pages: 0 });
          setPage(result.pagination?.page || pageNumber);
          setPageSize(result.pagination?.page_size || size);
          if (result.banks?.length) setBanks(result.banks);
        } else {
          toast.error(result.error || "Failed to load reconciliation dashboard");
        }
      } catch (error) {
        console.error("Error fetching reconciliation dashboard:", error);
        toast.error("Error fetching reconciliation dashboard");
      } finally {
        setSummaryLoading(false);
        setTableLoading(false);
      }
    },
    [currentTabKey, page, pageSize, appliedFilters, buildQueryParams]
  );

  useEffect(() => {
    fetchDashboard({ category: currentTabKey, pageNumber: page, size: pageSize });
  }, [currentTabKey, page, pageSize, appliedFilters]);

  const handleTabChange = (_, value) => {
    setActiveTab(value);
    setPage(1);
  };

  const handleRefresh = () => {
    fetchDashboard({
      category: currentTabKey,
      pageNumber: page,
      size: pageSize,
      refreshAll: true,
    });
  };

  const handleApplyFilters = () => {
    setAppliedFilters(filters);
    setPage(1);
  };

  const handleClearFilters = () => {
    setFilters(defaultFilters);
    setAppliedFilters(defaultFilters);
    setPage(1);
  };

  const handlePageChange = (_, value) => {
    setPage(value);
  };

  const handlePageSizeChange = (event) => {
    setPageSize(Number(event.target.value));
    setPage(1);
  };

  const handleExport = async () => {
    setExportLoading(true);
    try {
      const params = buildQueryParams(currentTabKey, 1, pageSize);
      const response = await fetch(
        `${BASE_URL}/fee_voucher/export_reconciliation_dashboard?${params.toString()}`,
        {
          method: "GET",
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      if (!response.ok) {
        const result = await response.json();
        toast.error(result.error || "Failed to export reconciliation data");
        return;
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = `reconciliation_${currentTabKey}_${new Date().toISOString().slice(0, 10)}.csv`;
      anchor.click();
      window.URL.revokeObjectURL(url);
      toast.success("Reconciliation data exported successfully");
    } catch (error) {
      console.error("Error exporting reconciliation data:", error);
      toast.error("Error exporting reconciliation data");
    } finally {
      setExportLoading(false);
    }
  };

  const openMarkPaidDialog = (record) => {
    setSelectedRecord(record);
    setMarkPaidForm({
      bank: record.bank_name && banks.includes(record.bank_name) ? record.bank_name : banks[0] || DEFAULT_BANKS[0],
      transaction_id: "",
      payment_date: new Date().toISOString().slice(0, 10),
    });
    setMarkPaidOpen(true);
  };

  const handleMarkPaid = async () => {
    if (!selectedRecord?.cms_source) {
      toast.error("Cannot mark paid: CMS voucher not found");
      return;
    }
    if (!markPaidForm.transaction_id.trim()) {
      toast.error("Transaction ID is required");
      return;
    }

    setActionLoading(true);
    try {
      const response = await fetch(`${BASE_URL}/fee_voucher/reconciliation_mark_paid`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          voucher_code: selectedRecord.voucher_code,
          cms_source: selectedRecord.cms_source,
          bank: markPaidForm.bank,
          transaction_id: markPaidForm.transaction_id.trim(),
          payment_date: markPaidForm.payment_date,
        }),
      });
      const result = await response.json();
      if (response.ok) {
        toast.success("Voucher marked as paid");
        setMarkPaidOpen(false);
        fetchDashboard({ category: currentTabKey, pageNumber: page, size: pageSize, refreshAll: true });
      } else {
        toast.error(result.error || "Failed to mark voucher as paid");
      }
    } catch (error) {
      console.error("Error marking voucher as paid:", error);
      toast.error("Error marking voucher as paid");
    } finally {
      setActionLoading(false);
    }
  };

  const handleMarkUnpaid = async (record) => {
    if (!record?.cms_source) {
      toast.error("Cannot mark unpaid: CMS voucher not found");
      return;
    }
    if (!window.confirm(`Mark voucher ${record.voucher_code} as unpaid?`)) return;

    setActionLoading(true);
    try {
      const response = await fetch(`${BASE_URL}/fee_voucher/reconciliation_mark_unpaid`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          voucher_code: record.voucher_code,
          cms_source: record.cms_source,
        }),
      });
      const result = await response.json();
      if (response.ok) {
        toast.success("Voucher marked as unpaid");
        fetchDashboard({ category: currentTabKey, pageNumber: page, size: pageSize, refreshAll: true });
      } else {
        toast.error(result.error || "Failed to mark voucher as unpaid");
      }
    } catch (error) {
      console.error("Error marking voucher as unpaid:", error);
      toast.error("Error marking voucher as unpaid");
    } finally {
      setActionLoading(false);
    }
  };

  const openAuditDialog = async (record) => {
    setSelectedRecord(record);
    setAuditOpen(true);
    setAuditLoading(true);
    setAuditLogs([]);

    try {
      const response = await fetch(
        `${BASE_URL}/fee_voucher/get_reconciliation_audit_logs?voucher_code=${encodeURIComponent(record.voucher_code)}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );
      const result = await response.json();
      if (response.ok) {
        setAuditLogs(result.logs || []);
      } else {
        toast.error(result.error || "Failed to load audit trail");
      }
    } catch (error) {
      console.error("Error loading audit trail:", error);
      toast.error("Error loading audit trail");
    } finally {
      setAuditLoading(false);
    }
  };

  const baseColumns = useMemo(
    () => [
      { Header: "#", accessor: "serial", align: "left", width: "5%" },
      { Header: "Voucher No", accessor: "voucher_code", align: "left" },
      { Header: "Bank PV No", accessor: "bank_pv_no", align: "left" },
      { Header: "CMS Source", accessor: "cms_source", align: "left" },
      { Header: "CMS Status", accessor: "cms_status", align: "center" },
      { Header: "Bank Status", accessor: "bank_status", align: "center" },
      { Header: "CMS Amount", accessor: "cms_amount", align: "right" },
      { Header: "Bank Amount", accessor: "bank_amount", align: "right" },
    ],
    []
  );

  const renderActions = (record) => (
    <div className="action-btn-group">
      <button
        type="button"
        className="action-btn action-btn-paid"
        disabled={!record.cms_source || actionLoading}
        onClick={() => openMarkPaidDialog(record)}
      >
        Mark Paid
      </button>
      <button
        type="button"
        className="action-btn action-btn-unpaid"
        disabled={!record.cms_source || actionLoading}
        onClick={() => handleMarkUnpaid(record)}
      >
        Mark Unpaid
      </button>
      <button
        type="button"
        className="action-btn action-btn-audit"
        onClick={() => openAuditDialog(record)}
      >
        <HistoryIcon style={{ fontSize: 14, verticalAlign: "middle", marginRight: 4 }} />
        Audit
      </button>
    </div>
  );

  const buildRows = (items, currentPage, size) =>
    items.length > 0
      ? items.map((record, index) => ({
          serial: (currentPage - 1) * size + index + 1,
          voucher_code: record.voucher_code || "-",
          bank_pv_no: record.bank_pv_no || record.reference_no || "-",
          cms_source: record.cms_source
            ? record.cms_source.charAt(0).toUpperCase() + record.cms_source.slice(1)
            : "-",
          cms_status: statusChip(
            record.cms_status || "-",
            record.cms_status === "Paid" ? "success" : record.cms_status === "Not Found" ? "default" : "warning"
          ),
          bank_status: statusChip(
            record.bank_status || "-",
            record.bank_status === "Credited" ? "success" : "error"
          ),
          cms_amount: formatAmount(record.cms_amount),
          bank_amount: formatAmount(record.bank_amount),
          payment_date: formatDate(record.payment_date),
          bank_date: formatDate(record.bank_date),
          issue_type: record.issue_type || "-",
          description: record.description || "-",
          cms_transaction_id: record.cms_transaction_id || "-",
          bank_transaction_id: record.bank_transaction_id || "-",
          cms_count: record.cms_count ?? "-",
          bank_count: record.bank_count ?? "-",
          bank_name: record.bank_name || "-",
          actions: renderActions(record),
        }))
      : [
          {
            serial: "-",
            voucher_code: "-",
            bank_pv_no: "-",
            cms_source: "-",
            cms_status: "-",
            bank_status: "-",
            cms_amount: "-",
            bank_amount: "-",
            payment_date: "-",
            bank_date: "-",
            issue_type: "-",
            description: "-",
            cms_transaction_id: "-",
            bank_transaction_id: "-",
            cms_count: "-",
            bank_count: "-",
            bank_name: "-",
            actions: "-",
          },
        ];

  const tabConfig = useMemo(
    () => ({
      reconciled: {
        description:
          "CMS voucher matched with bank record by Voucher No / PV No and amount. CMS Status = Paid and Bank Status = Credited.",
        color: "success",
        columns: [
          ...baseColumns,
          { Header: "Payment Date", accessor: "payment_date", align: "left" },
          { Header: "Bank Date", accessor: "bank_date", align: "left" },
          { Header: "Actions", accessor: "actions", align: "left" },
        ],
      },
      unreconciled: {
        description:
          "CMS paid but bank not found, or bank credited but CMS unpaid. These require further investigation.",
        color: "warning",
        columns: [
          ...baseColumns,
          { Header: "Issue", accessor: "issue_type", align: "left" },
          { Header: "Payment Date", accessor: "payment_date", align: "left" },
          { Header: "Bank Date", accessor: "bank_date", align: "left" },
          { Header: "Actions", accessor: "actions", align: "left" },
        ],
      },
      duplicate: {
        description:
          "Same Voucher No / PV No appearing multiple times. Flagged for duplicate payment handling.",
        color: "info",
        columns: [
          ...baseColumns,
          { Header: "CMS Count", accessor: "cms_count", align: "center" },
          { Header: "Bank Count", accessor: "bank_count", align: "center" },
          { Header: "Issue", accessor: "issue_type", align: "left" },
          { Header: "Actions", accessor: "actions", align: "left" },
        ],
      },
      mismatch: {
        description:
          "PV No / Voucher No mismatch, amount mismatch, or status inconsistency between CMS and bank records.",
        color: "error",
        columns: [
          ...baseColumns,
          { Header: "Issue", accessor: "issue_type", align: "left" },
          { Header: "Description", accessor: "description", align: "left" },
          { Header: "Actions", accessor: "actions", align: "left" },
        ],
      },
    }),
    [baseColumns, actionLoading, banks]
  );

  const currentTab = tabConfig[currentTabKey];
  const tableRows = buildRows(records, pagination.page || page, pagination.page_size || pageSize);
  const entriesStart = pagination.total === 0 ? 0 : (pagination.page - 1) * pagination.page_size + 1;
  const entriesEnd = Math.min(pagination.page * pagination.page_size, pagination.total);

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <ToastContainer />
      <style>{`
        .reconciliation-dashboard .header-actions {
          display: flex;
          gap: 8px;
          flex-wrap: wrap;
        }
        .reconciliation-dashboard .btn-export {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          padding: 10px 18px;
          font-size: 14px;
          font-weight: 500;
          color: #7928ca;
          background: #fff;
          border: 1px solid #7928ca;
          border-radius: 8px;
          cursor: pointer;
        }
        .reconciliation-dashboard .btn-export:hover:not(:disabled) {
          background: #f3e8ff;
        }
        .reconciliation-dashboard .btn-export:disabled,
        .reconciliation-dashboard .btn-refresh:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }
        .reconciliation-dashboard .btn-refresh {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          padding: 10px 18px;
          min-width: 120px;
          justify-content: center;
          font-size: 14px;
          font-weight: 500;
          color: #fff;
          background: #7928ca;
          border: none;
          border-radius: 8px;
          cursor: pointer;
          box-shadow: 0 2px 4px rgba(121, 40, 202, 0.25);
        }
        .reconciliation-dashboard .btn-refresh:hover:not(:disabled) {
          background: #6819b8;
        }
        .reconciliation-dashboard .filter-section-title {
          font-size: 1.125rem;
          font-weight: 600;
          color: #344767;
          margin-bottom: 16px;
        }
        .reconciliation-dashboard .filter-grid {
          display: flex;
          flex-wrap: wrap;
          gap: 16px;
          align-items: flex-end;
          margin-bottom: 24px;
        }
        .reconciliation-dashboard .filter-field {
          flex: 1 1 150px;
          min-width: 150px;
          max-width: 200px;
        }
        .reconciliation-dashboard .filter-label {
          display: block;
          font-size: 12px;
          font-weight: 600;
          color: #344767;
          margin-bottom: 6px;
        }
        .reconciliation-dashboard .filter-field-search {
          flex: 1 1 220px;
          min-width: 220px;
          max-width: 280px;
        }
        .reconciliation-dashboard .filter-input-search {
          width: 100%;
          height: 40px;
          padding: 8px 12px;
          border: 1px solid #d2d6da;
          border-radius: 8px;
          font-size: 14px;
          color: #344767;
          background: #fff;
          box-sizing: border-box;
          outline: none;
        }
        .reconciliation-dashboard .filter-input-search:focus {
          border-color: #7928ca;
          box-shadow: 0 0 0 2px rgba(121, 40, 202, 0.15);
        }
        .reconciliation-dashboard .filter-input-search::placeholder {
          color: #8392ab;
        }
        .reconciliation-dashboard .filter-input,
        .reconciliation-dashboard .filter-select {
          width: 100%;
          height: 40px;
          padding: 8px 12px;
          border: 1px solid #d2d6da;
          border-radius: 8px;
          font-size: 14px;
          color: #344767;
          background: #fff;
          box-sizing: border-box;
          outline: none;
        }
        .reconciliation-dashboard .filter-input:focus,
        .reconciliation-dashboard .filter-select:focus {
          border-color: #7928ca;
          box-shadow: 0 0 0 2px rgba(121, 40, 202, 0.15);
        }
        .reconciliation-dashboard .filter-actions {
          display: flex;
          gap: 8px;
          align-items: center;
          flex: 0 0 auto;
          padding-bottom: 2px;
        }
        .reconciliation-dashboard .btn-apply {
          padding: 10px 22px;
          font-size: 14px;
          font-weight: 600;
          color: #fff;
          background: #7928ca;
          border: none;
          border-radius: 8px;
          cursor: pointer;
          box-shadow: 0 2px 4px rgba(121, 40, 202, 0.25);
        }
        .reconciliation-dashboard .btn-apply:hover {
          background: #6819b8;
        }
        .reconciliation-dashboard .btn-clear {
          padding: 10px 22px;
          font-size: 14px;
          font-weight: 600;
          color: #344767;
          background: #fff;
          border: 1px solid #344767;
          border-radius: 8px;
          cursor: pointer;
        }
        .reconciliation-dashboard .btn-clear:hover {
          background: #f8f9fa;
        }
        .reconciliation-dashboard .action-btn-group {
          display: flex;
          flex-wrap: wrap;
          gap: 6px;
          align-items: center;
        }
        .reconciliation-dashboard .action-btn {
          display: inline-flex;
          align-items: center;
          padding: 6px 12px;
          font-size: 12px;
          font-weight: 600;
          border-radius: 6px;
          cursor: pointer;
          border: 1px solid;
          background: #fff;
          white-space: nowrap;
          line-height: 1.4;
        }
        .reconciliation-dashboard .action-btn-paid {
          color: #2e7d32;
          border-color: #2e7d32;
        }
        .reconciliation-dashboard .action-btn-paid:hover:not(:disabled) {
          background: #e8f5e9;
        }
        .reconciliation-dashboard .action-btn-unpaid {
          color: #ed6c02;
          border-color: #ed6c02;
        }
        .reconciliation-dashboard .action-btn-unpaid:hover:not(:disabled) {
          background: #fff3e0;
        }
        .reconciliation-dashboard .action-btn-audit {
          color: #0288d1;
          border-color: #0288d1;
        }
        .reconciliation-dashboard .action-btn-audit:hover:not(:disabled) {
          background: #e1f5fe;
        }
        .reconciliation-dashboard .action-btn:disabled {
          opacity: 0.45;
          cursor: not-allowed;
        }
        .reconciliation-dashboard .pagination-bar {
          display: flex;
          flex-wrap: wrap;
          justify-content: space-between;
          align-items: center;
          margin-top: 16px;
          padding-top: 16px;
          border-top: 1px solid #e0e0e0;
          gap: 12px;
        }
        .reconciliation-dashboard .page-size-select {
          min-width: 80px;
          height: 40px;
          padding: 8px 12px;
          border: 1px solid #d2d6da;
          border-radius: 8px;
          font-size: 14px;
          color: #344767;
          background: #fff;
        }
        .audit-trail-dialog-content {
          padding-top: 8px !important;
        }
        .audit-trail-table-container {
          width: 100%;
          overflow-x: auto;
        }
        .audit-trail-table {
          width: 100%;
          table-layout: fixed;
          border-collapse: collapse;
          border-spacing: 0;
        }
        .audit-trail-table thead {
          display: table-header-group;
        }
        .audit-trail-table tbody {
          display: table-row-group;
        }
        .audit-trail-table tr {
          display: table-row;
        }
        .audit-trail-table th,
        .audit-trail-table td {
          display: table-cell;
          padding: 10px 12px;
          text-align: left;
          vertical-align: top;
          border-bottom: 1px solid #e9ecef;
          box-sizing: border-box;
        }
        .audit-trail-table thead th {
          font-size: 12px;
          font-weight: 700;
          color: #8392ab;
          text-transform: uppercase;
          letter-spacing: 0.4px;
          background: #f8f9fa;
          white-space: nowrap;
        }
        .audit-trail-table tbody td {
          font-size: 13px;
          color: #344767;
          word-break: break-word;
        }
        .audit-trail-table .col-date {
          width: 22%;
        }
        .audit-trail-table .col-action {
          width: 28%;
        }
        .audit-trail-table .col-user {
          width: 18%;
        }
        .audit-trail-table .col-details {
          width: 32%;
        }
      `}</style>
      <div className="reconciliation-dashboard">
      <MDBox py={3}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Card sx={{ p: 3 }}>
              <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={2} flexWrap="wrap" gap={1}>
                <MDTypography variant="h5" fontWeight="medium">
                  Voucher Reconciliation Dashboard
                </MDTypography>
                <div className="header-actions">
                  <button
                    type="button"
                    className="btn-export"
                    onClick={handleExport}
                    disabled={exportLoading || tableLoading}
                  >
                    {exportLoading ? <CircularProgress size={18} /> : <><FileDownloadIcon style={{ fontSize: 18 }} /> Export Excel</>}
                  </button>
                  <button
                    type="button"
                    className="btn-refresh"
                    onClick={handleRefresh}
                    disabled={summaryLoading || tableLoading}
                  >
                    {summaryLoading ? <CircularProgress size={20} style={{ color: "#fff" }} /> : <><RefreshIcon style={{ fontSize: 18 }} /> Refresh</>}
                  </button>
                </div>
              </MDBox>

              <MDTypography variant="body2" color="text" mb={3}>
                Compares CMS vouchers (Admission &amp; Hostel) against uploaded bank records using Voucher No / PV No and amount.
                Bank PV No is matched with CMS voucher code.
              </MDTypography>

              <Grid container spacing={3} mb={3}>
                <Grid item xs={12} sm={6} md={3}>
                  <ComplexStatisticsCard
                    color="success"
                    icon="check_circle"
                    title="Reconciled"
                    count={summary.reconciled ?? 0}
                    percentage={{ color: "success", amount: "", label: "Matched by PV No & amount" }}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <ComplexStatisticsCard
                    color="warning"
                    icon="warning"
                    title="Unreconciled"
                    count={summary.unreconciled ?? 0}
                    percentage={{ color: "warning", amount: "", label: "Needs investigation" }}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <ComplexStatisticsCard
                    color="info"
                    icon="content_copy"
                    title="Duplicate"
                    count={summary.duplicate ?? 0}
                    percentage={{ color: "info", amount: "", label: "Duplicate voucher / PV No" }}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <ComplexStatisticsCard
                    color="error"
                    icon="error_outline"
                    title="Mismatch"
                    count={summary.mismatch ?? 0}
                    percentage={{ color: "error", amount: "", label: "Data inconsistencies" }}
                  />
                </Grid>
              </Grid>

              <Alert severity="info" sx={{ mb: 0 }}>
                CMS records: {summary.total_cms_vouchers ?? 0} | Bank records with PV No:{" "}
                {summary.total_bank_records ?? 0}
              </Alert>
            </Card>
          </Grid>

          <Grid item xs={12}>
            <Card style={{ padding: 24 }}>
              <div className="filter-section-title">Filters</div>
              <div className="filter-grid">
                <div className="filter-field filter-field-search">
                  <label className="filter-label" htmlFor="filter-voucher-search">Search Voucher</label>
                  <input
                    id="filter-voucher-search"
                    type="text"
                    className="filter-input-search"
                    placeholder="Voucher No / PV No"
                    value={filters.voucherSearch}
                    onChange={(e) => setFilters((prev) => ({ ...prev, voucherSearch: e.target.value }))}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") handleApplyFilters();
                    }}
                  />
                </div>
                <div className="filter-field">
                  <label className="filter-label" htmlFor="filter-start-date">Start Date</label>
                  <input
                    id="filter-start-date"
                    type="date"
                    className="filter-input"
                    value={filters.startDate}
                    onChange={(e) => setFilters((prev) => ({ ...prev, startDate: e.target.value }))}
                  />
                </div>
                <div className="filter-field">
                  <label className="filter-label" htmlFor="filter-end-date">End Date</label>
                  <input
                    id="filter-end-date"
                    type="date"
                    className="filter-input"
                    value={filters.endDate}
                    onChange={(e) => setFilters((prev) => ({ ...prev, endDate: e.target.value }))}
                  />
                </div>
                <div className="filter-field">
                  <label className="filter-label" htmlFor="filter-bank">Bank</label>
                  <select
                    id="filter-bank"
                    className="filter-select"
                    value={filters.bank}
                    onChange={(e) => setFilters((prev) => ({ ...prev, bank: e.target.value }))}
                  >
                    <option value="all">All Banks</option>
                    {banks.map((bank) => (
                      <option key={bank} value={bank}>
                        {bank}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="filter-field">
                  <label className="filter-label" htmlFor="filter-cms-status">CMS Status</label>
                  <select
                    id="filter-cms-status"
                    className="filter-select"
                    value={filters.cmsStatus}
                    onChange={(e) => setFilters((prev) => ({ ...prev, cmsStatus: e.target.value }))}
                  >
                    <option value="all">All</option>
                    <option value="paid">Paid</option>
                    <option value="unpaid">Unpaid</option>
                  </select>
                </div>
                <div className="filter-field">
                  <label className="filter-label" htmlFor="filter-bank-status">Bank Status</label>
                  <select
                    id="filter-bank-status"
                    className="filter-select"
                    value={filters.bankStatus}
                    onChange={(e) => setFilters((prev) => ({ ...prev, bankStatus: e.target.value }))}
                  >
                    <option value="all">All</option>
                    <option value="credited">Credited</option>
                    <option value="not_found">Not Found</option>
                  </select>
                </div>
                <div className="filter-actions">
                  <button type="button" className="btn-apply" onClick={handleApplyFilters}>
                    Apply
                  </button>
                  <button type="button" className="btn-clear" onClick={handleClearFilters}>
                    Clear
                  </button>
                </div>
              </div>

              <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 2 }}>
                <Tabs
                  value={activeTab}
                  onChange={handleTabChange}
                  variant="scrollable"
                  scrollButtons="auto"
                >
                  <Tab label={`Reconciled (${summary.reconciled ?? 0})`} icon={<CheckCircleIcon />} iconPosition="start" />
                  <Tab label={`Unreconciled (${summary.unreconciled ?? 0})`} icon={<WarningAmberIcon />} iconPosition="start" />
                  <Tab label={`Duplicate (${summary.duplicate ?? 0})`} icon={<ContentCopyIcon />} iconPosition="start" />
                  <Tab label={`Mismatch (${summary.mismatch ?? 0})`} icon={<ErrorOutlineIcon />} iconPosition="start" />
                </Tabs>
              </Box>

              <Alert severity={currentTab.color} sx={{ mb: 2 }}>
                {currentTab.description}
              </Alert>

              {tableLoading ? (
                <MDBox display="flex" justifyContent="center" py={4}>
                  <CircularProgress />
                </MDBox>
              ) : (
                <>
                  <DataTable
                    table={{ columns: currentTab.columns, rows: tableRows }}
                    isSorted={false}
                    entriesPerPage={false}
                    showTotalEntries={false}
                    noEndBorder
                  />

                  <div className="pagination-bar">
                    <MDTypography variant="button" color="secondary" fontWeight="regular">
                      {pagination.total > 0
                        ? `Showing ${entriesStart} to ${entriesEnd} of ${pagination.total} entries`
                        : "No entries found"}
                    </MDTypography>

                    <MDBox display="flex" alignItems="center" flexWrap="wrap" style={{ gap: 16 }}>
                      <MDBox display="flex" alignItems="center" style={{ gap: 8 }}>
                        <MDTypography variant="body2">Rows per page</MDTypography>
                        <select
                          className="page-size-select"
                          value={pageSize}
                          onChange={handlePageSizeChange}
                        >
                          {PAGE_SIZE_OPTIONS.map((size) => (
                            <option key={size} value={size}>
                              {size}
                            </option>
                          ))}
                        </select>
                      </MDBox>

                      {pagination.total_pages > 1 && (
                        <Pagination
                          count={pagination.total_pages}
                          page={pagination.page}
                          onChange={handlePageChange}
                          color="primary"
                          variant="outlined"
                          shape="rounded"
                          showFirstButton
                          showLastButton
                        />
                      )}
                    </MDBox>
                  </div>
                </>
              )}
            </Card>
          </Grid>
        </Grid>
      </MDBox>

      <Dialog open={markPaidOpen} onClose={() => setMarkPaidOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Mark Voucher as Paid</DialogTitle>
        <DialogContent>
          <MDTypography variant="body2" mb={2}>
            Voucher: <strong>{selectedRecord?.voucher_code}</strong> ({selectedRecord?.cms_source})
          </MDTypography>
          <Grid container spacing={2} mt={0.5}>
            <Grid item xs={12}>
              <MDTypography variant="caption" color="text" display="block" mb={0.5}>
                Bank
              </MDTypography>
              <Select
                fullWidth
                size="small"
                value={markPaidForm.bank}
                onChange={(e) => setMarkPaidForm((prev) => ({ ...prev, bank: e.target.value }))}
              >
                {banks.map((bank) => (
                  <MenuItem key={bank} value={bank}>
                    {bank}
                  </MenuItem>
                ))}
              </Select>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                size="small"
                label="Transaction ID"
                value={markPaidForm.transaction_id}
                onChange={(e) => setMarkPaidForm((prev) => ({ ...prev, transaction_id: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                size="small"
                label="Payment Date"
                type="date"
                value={markPaidForm.payment_date}
                onChange={(e) => setMarkPaidForm((prev) => ({ ...prev, payment_date: e.target.value }))}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMarkPaidOpen(false)} sx={{ textTransform: "none" }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="success"
            onClick={handleMarkPaid}
            disabled={actionLoading}
            sx={{ textTransform: "none" }}
          >
            {actionLoading ? <CircularProgress size={18} color="inherit" /> : "Confirm Paid"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={auditOpen} onClose={() => setAuditOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Audit Trail - Voucher {selectedRecord?.voucher_code}</DialogTitle>
        <DialogContent className="audit-trail-dialog-content">
          {auditLoading ? (
            <MDBox display="flex" justifyContent="center" py={4}>
              <CircularProgress />
            </MDBox>
          ) : auditLogs.length === 0 ? (
            <Alert severity="info">No audit records found for this voucher.</Alert>
          ) : (
            <div className="audit-trail-table-container">
              <table className="audit-trail-table">
                <thead>
                  <tr>
                    <th className="col-date">Date</th>
                    <th className="col-action">Action</th>
                    <th className="col-user">Performed By</th>
                    <th className="col-details">Details</th>
                  </tr>
                </thead>
                <tbody>
                  {auditLogs.map((log) => (
                    <tr key={log.id}>
                      <td className="col-date">{formatDateTime(log.createdAt)}</td>
                      <td className="col-action">{log.action}</td>
                      <td className="col-user">{log.performed_by_name || log.performed_by || "-"}</td>
                      <td className="col-details">
                        {log.new_data?.cms_source ? `Source: ${log.new_data.cms_source}` : "-"}
                        {log.new_data?.data?.is_paid !== undefined ? ` | is_paid: ${log.new_data.data.is_paid}` : ""}
                        {log.new_data?.data?.voucher_status ? ` | status: ${log.new_data.data.voucher_status}` : ""}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAuditOpen(false)} sx={{ textTransform: "none" }}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
      </div>
    </DashboardLayout>
  );
}

export default ReconciliationDashboard;
